import { useState } from "react";
import { useFormik } from "formik";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormGroup,
  FormLabel,
  LinearProgress,
  TextField,
  Typography,
} from "@material-ui/core";

import PerfectScrollbar from "react-perfect-scrollbar";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";

export default function FormDenyListDialog({
  open,
  formType,
  onClose,
  selectedEmail,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [domainDisabled, setDomainDisabled] = useState(false);
  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem } = useLogs();

  const deleteEmail = async (email) => {
    setSubmitting(true);

    await Api.delete("deny-contact-delete", "denylist", { email }, true)
      .then((res) => {
        if (res.Success) {
          snackbar.showMessage(res?.Result?.MessageResponse, {
            severity: "success",
          });
          setSubmitting(false);
          handleClose("delete");
        } else {
          snackbar.showMessage("Ocorreu um erro ao excluir o e-mail.", {
            severity: "error",
          });
          setSubmitting(false);
        }
      })
      .catch((error) => {
        snackbar.showMessage(`Ocorreu um erro ao excluir o e-mail.`, {
          severity: "error",
        });
        setSubmitting(false);
        setLocalStorageApiProblem({
          idMenuAccess: "141",
          screen: "Deny List",
          problem: `Erro na chamada do endpoint deny-contact-delete - ${error}`,
        });
      });
  };

  const submit = async (values) => {
    const { email, dominio } = values;
    const emailDominio = email || dominio;

    setSubmitting(true);

    if (formType === "delete") {
      return deleteEmail(selectedEmail?.email);
    }

    return await Api.post(
      "deny-contact-create",
      "denylist",
      { email: emailDominio },
      true
    )
      .then((res) => {
        if (res.Success) {
          snackbar.showMessage(res?.Result?.MessageResponse, {
            severity: "success",
          });
          setSubmitting(false);
          handleClose("create");
        } else {
          snackbar.showMessage(
            `Ocorreu um erro ao cadastrar o e-mail. Verifique os parâmetros informados.`,
            {
              severity: "error",
            }
          );
          setSubmitting(false);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setSubmitting(false);
        setLocalStorageApiProblem({
          idMenuAccess: "141",
          screen: "Deny List",
          problem: `Erro na chamada do endpoint deny-contact-create - ${error}`,
        });
      });
  };

  const formik = useFormik({
    initialValues: {
      email: selectedEmail?.email || "",
      domain: "",
    },
    onSubmit: submit,
  });

  const { values, handleChange, handleSubmit, dirty, resetForm } = formik;

  const handleEmailChange = (event) => {
    handleChange(event);
    setDomainDisabled(event.target.value !== "");
  };

  const handleDomainChange = (event) => {
    handleChange(event);
    setEmailDisabled(event.target.value !== "");
  };

  const handleClose = (updatedEmail) => {
    resetForm();
    onClose(updatedEmail);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <span>
              <Typography id="alert-dialog-title" variant="h3" component="h3">
                {`${
                  formType === "delete" ? "Excluir" : "Cadastrar"
                } E-mail ou Dominio`}
              </Typography>
            </span>
          </DialogTitle>
          <PerfectScrollbar>
            <DialogContent style={{ padding: "1rem 1.5rem 1.5rem" }}>
              {formType === "delete" ? (
                <Typography id="alert-dialog-description" variant="h4">
                  Confirmar exclusão do E-mail?
                </Typography>
              ) : (
                <>
                  <Grid container>
                    <Grid item xs={12}>
                      <FormGroup>
                        <FormLabel>E-mail</FormLabel>
                        <TextField
                          name="email"
                          type={"email"}
                          fullWidth
                          disabled={emailDisabled || submitting}
                          value={values.email}
                          variant="outlined"
                          onChange={handleEmailChange}
                          placeholder="email@dominio.com.br"
                          onInput={(e) => {
                            e.target.value = e.target.value
                              .toString()
                              .slice(0, 300);
                          }}
                        />
                      </FormGroup>
                    </Grid>

                    <Grid item xs={12}>
                      <FormGroup>
                        <FormLabel>Domínio</FormLabel>
                        <TextField
                          name="dominio"
                          type={"text"}
                          fullWidth
                          disabled={domainDisabled || submitting}
                          value={values.dominio}
                          variant="outlined"
                          onChange={handleDomainChange}
                          placeholder="@dominio.com.br"
                          onInput={(e) => {
                            e.target.value = e.target.value
                              .toString()
                              .slice(0, 300);
                          }}
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </>
              )}
              {submitting && (
                <div
                  style={{ marginTop: formType === "delete" ? "2rem" : "1rem" }}
                >
                  <span>{`${
                    formType === "create" ? "Cadastrando" : "Deletando"
                  } e-mail...`}</span>
                  <LinearProgress />
                </div>
              )}
            </DialogContent>
          </PerfectScrollbar>
          <DialogActions>
            <Button component={Box} onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            {formType === "delete" ? (
              <Button
                color="default"
                variant="contained"
                type="submit"
                disabled={submitting}
              >
                Confirmar
              </Button>
            ) : (
              <Button
                color="default"
                variant="contained"
                type="submit"
                disabled={
                  submitting ||
                  !dirty ||
                  MaskAndValid.isNullOrEmpty(values.email || values.dominio) ||
                  (values.dominio && !/^@(\w{4,}).*/.test(values.dominio))
                }
              >
                Cadastrar
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
